import { dxtToLocalServerTime, MesfModal } from "@dexteel/mesf-core";
import PhotoCameraRounded from "@mui/icons-material/PhotoCameraRounded";
import {
	Alert,
	type AlertColor,
	Box,
	Button,
	Grid2,
	LinearProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	ChangeEvent,
	KeyboardEvent,
	type ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { WorkOrderMatchLabel } from "./WorkOrderMatch";
import { getCatalogGrades } from "@/shared/repositories/CatalogGradeRepository";
import { getCatalogSections } from "@/shared/repositories/CatalogSectionRepository";
import { getProducts } from "@/shared/repositories/ProductRepository";
import { useScanBurst } from "../hooks/useScanBurst";
import {
	getSawEntryScanPreview,
	type SawEntryScanPreview,
	upsertSawEntryScan,
} from "../repositories/sawWriteRepository";
import {
	ENTRY_BARCODE_DIGITS,
	formatEntryLength,
	isSawEntryQr,
	type ParsedSawEntryBarcode,
	parseSawEntryTag,
} from "../utils/entryBarcode";

type Props = {
	open: boolean;
	// Real saw AssetId; null disables the Save (catAsset not wired yet).
	assetId: number | null;
	onClose: () => void;
	onScanned?: () => void;
	onError?: (e: Error) => void;
	// Tablet: checks the tag against the queue, adds it and stays open for the next one.
	requireEmployee?: (run: (employee: string) => void) => void;
};

const EMPTY = "—";

// Longer pause between keys = a new scan.
const SCAN_GAP_MS = 300;

// Mill codes can carry leading zeros the catalog may omit (or vice-versa).
const normCode = (s: string) => s.trim().replace(/^0+(?=\d)/, "");

// Space/case-insensitive: the QR may or may not carry the part's spaces.
const normPart = (s: string) => s.replace(/\s+/g, "").toUpperCase();

const cleanTag = (s: string) => s.replace(/[^0-9A-Za-z. ()\-]/g, "");

const PreviewRow = ({
	label,
	value,
	valid = null,
	touch = false,
}: {
	label: string;
	value: ReactNode;
	// true = found in catalog, false = not found, null = no check.
	valid?: boolean | null;
	touch?: boolean;
}) => (
	<Grid2 size={{ xs: 6 }}>
		<Typography
			sx={{
				fontSize: touch ? "0.8rem" : "0.62rem",
				fontWeight: 700,
				color: "#6b7280",
				textTransform: "uppercase",
				letterSpacing: 0.4,
			}}
		>
			{label}
		</Typography>
		<Typography
			sx={{
				fontSize: touch ? "1.375rem" : "1.05rem",
				fontWeight: 700,
				color:
					valid === false ? "#d32f2f" : valid === true ? "#2e7d32" : "#1f2937",
				fontVariantNumeric: "tabular-nums",
				overflowWrap: "anywhere",
			}}
		>
			{value}
		</Typography>
		{valid === false && (
			<Typography
				sx={{
					fontSize: touch ? "0.8rem" : "0.6rem",
					fontWeight: 600,
					color: "#d32f2f",
				}}
			>
				Not in catalog
			</Typography>
		)}
	</Grid2>
);

// Wedge scanner fills the input, the preview decodes it live, Save confirms.
export const ScanEntryBundleModal = ({
	open,
	assetId,
	onClose,
	onScanned,
	onError,
	requireEmployee,
}: Props) => {
	const queryClient = useQueryClient();
	const [code, setCode] = useState("");
	const [added, setAdded] = useState<string | null>(null);
	const [reading, setReading] = useState(false);
	const [photoAlert, setPhotoAlert] = useState<{
		severity: AlertColor;
		text: string;
	} | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const photoRef = useRef<HTMLInputElement>(null);
	const saveRef = useRef<HTMLButtonElement>(null);
	const scanBurst = useScanBurst(SCAN_GAP_MS);
	const touch = requireEmployee != null;

	// Reset + focus on open so the scanner streams straight into the field.
	useEffect(() => {
		if (!open) return;
		setCode("");
		setAdded(null);
		setPhotoAlert(null);
		const t = setTimeout(() => inputRef.current?.focus(), 50);
		return () => clearTimeout(t);
	}, [open]);

	const parsed = parseSawEntryTag(code);
	const digits = code.replace(/\D/g, "").length;
	const isQr = isSawEntryQr(code);

	// Only complete tags parse, so this runs once per scan, not per keystroke.
	const previewQuery = useQuery<SawEntryScanPreview, Error>({
		queryKey: ["sawEntryScanPreview", assetId, code],
		queryFn: () => getSawEntryScanPreview(assetId!, parsed!),
		enabled: open && touch && parsed != null && assetId != null,
		cacheTime: 0,
		retry: false,
	});
	const preview = previewQuery.isFetching ? undefined : previewQuery.data;
	const blocked = preview?.IsDuplicate
		? "Already in the queue"
		: preview?.AlreadyLoadedAt
			? `Already loaded ${dxtToLocalServerTime(preview.AlreadyLoadedAt, "MMM d HH:mm")}`
			: preview && preview.Count >= preview.Limit
				? `Queue is full (${preview.Count} of ${preview.Limit})`
				: null;
	// Ready to confirm → focus Save, so the operator's Enter (not the scanner's) saves.
	const ready = touch ? preview != null && !blocked : parsed != null;
	useEffect(() => {
		if (!open || !ready || assetId == null) return;
		const t = setTimeout(() => saveRef.current?.focus(), 250);
		return () => clearTimeout(t);
	}, [open, ready, assetId]);

	// Validate the scanned section/grade codes against our catalogs.
	const sectionsQuery = useQuery({
		queryKey: ["catalogSections"],
		queryFn: getCatalogSections,
		enabled: open,
		staleTime: 5 * 60 * 1000,
	});
	const gradesQuery = useQuery({
		queryKey: ["catalogGrades"],
		queryFn: getCatalogGrades,
		enabled: open,
		staleTime: 5 * 60 * 1000,
	});
	// QR tags carry a part number — check it exists before saving blind.
	const productsQuery = useQuery({
		queryKey: ["products"],
		queryFn: getProducts,
		enabled: open,
		staleTime: 5 * 60 * 1000,
	});

	const sectionCodes = useMemo(
		() =>
			new Set((sectionsQuery.data ?? []).map((s) => normCode(s.SectionName))),
		[sectionsQuery.data],
	);
	const gradeCodes = useMemo(
		() => new Set((gradesQuery.data ?? []).map((g) => normCode(g.GradeName))),
		[gradesQuery.data],
	);

	// null until the scan carries the code and the catalog has loaded.
	const sectionKnown =
		parsed?.sectionName != null && sectionsQuery.isSuccess
			? sectionCodes.has(normCode(parsed.sectionName))
			: null;
	const gradeKnown =
		parsed?.gradeName != null && gradesQuery.isSuccess
			? gradeCodes.has(normCode(parsed.gradeName))
			: null;
	const scannedPart =
		parsed?.partNumber != null ? normPart(parsed.partNumber) : null;
	const partKnown =
		scannedPart != null && productsQuery.isSuccess
			? productsQuery.data.some(
					(p) => p.PartNumber != null && normPart(p.PartNumber) === scannedPart,
				)
			: null;
	// Tablet also shows the product a mill tag resolves to.
	const partNumber = parsed?.partNumber ?? preview?.PartNumber ?? null;

	const mutation = useMutation<
		unknown,
		Error,
		{ tag: ParsedSawEntryBarcode; employee?: string }
	>({
		mutationFn: ({ tag, employee }) =>
			upsertSawEntryScan(assetId!, tag, employee),
		onSuccess: (_, { tag }) => {
			setCode("");
			if (touch) {
				setAdded(
					`Tag ${tag.tagNo} added · ${(previewQuery.data?.Count ?? 0) + 1} of ${previewQuery.data?.Limit}`,
				);
				return;
			}
			onScanned?.();
			onClose();
		},
		onError: (e) => onError?.(e),
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: ["sawEntryQueue"] }),
	});

	// Back to the field for the next tag, once it's enabled again.
	useEffect(() => {
		if (added && !mutation.isLoading) inputRef.current?.focus();
	}, [added, mutation.isLoading]);

	const save = () => {
		if (!parsed || !canSave) return;
		if (requireEmployee)
			requireEmployee((employee) => mutation.mutate({ tag: parsed, employee }));
		else mutation.mutate({ tag: parsed });
	};

	// The scanner's trailing Enter must NOT auto-save — the operator reviews the
	// preview first. Swallow it so it doesn't submit a surrounding form.
	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		const burst = scanBurst(e);
		if (e.key === "Enter") e.preventDefault();
		// Tablet has no keyboard to clear the field: a new scan replaces the last tag.
		else if (touch && !burst) inputRef.current?.select();
	};
	// Next scan while Add has focus: send it to the field, or its Enter would add this tag.
	const onSaveKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
		if (e.key.length !== 1) return;
		inputRef.current?.focus();
		inputRef.current?.select();
		scanBurst(e);
	};

	const setTag = (tag: string) => {
		setCode(tag);
		setAdded(null);
		setPhotoAlert(null);
		mutation.reset();
	};

	// No HTTPS → no live camera; the tablet's own camera app hands back a photo.
	const onPhoto = async (e: ChangeEvent<HTMLInputElement>) => {
		const photo = e.target.files?.[0];
		// Reset so a retake fires onChange again
		e.target.value = "";
		if (!photo) return;
		setReading(true);
		setPhotoAlert(null);
		try {
			const { readPhotoBarcodes } = await import("../utils/readTagPhoto");
			const tag = (await readPhotoBarcodes(photo))
				.map(cleanTag)
				.find((t) => parseSawEntryTag(t) != null);
			if (tag) setTag(tag);
			else
				setPhotoAlert({
					severity: "warning",
					text: "No tag QR found in the photo — retake it closer",
				});
		} catch (err) {
			// A redeploy renames the lazy chunk; only a reload fetches the new one.
			setPhotoAlert({
				severity: "error",
				text: `Unable to read the photo, reload the page: ${err instanceof Error ? err.message : String(err)}`,
			});
		} finally {
			setReading(false);
			// Disabling the button dropped focus; the scanner gun needs the field.
			inputRef.current?.focus();
		}
	};

	const helper =
		assetId == null
			? "Saw asset not wired — scanning disabled"
			: code.length === 0
				? `Waiting for scan — ${ENTRY_BARCODE_DIGITS}-digit tag or QR`
				: parsed
					? touch
						? previewQuery.isError
							? "Unable to check the tag"
							: preview
								? blocked
									? "Tag can't be added"
									: "✓ Valid tag — tap Add to queue"
								: "Checking tag…"
						: "✓ Valid tag — press Enter to save"
					: isQr
						? "Incomplete QR tag"
						: `${digits} / ${ENTRY_BARCODE_DIGITS} digits`;

	const canSave = open && ready && assetId != null && !mutation.isLoading;
	const close = () => {
		if (!mutation.isLoading && !reading) onClose();
	};

	const field = (
		<TextField
			inputRef={inputRef}
			label="Bundle Tag Barcode"
			placeholder={touch ? "Scan the tag…" : "Scan or type the tag…"}
			value={code}
			onChange={(e) => setTag(cleanTag(e.target.value))}
			onKeyDown={onKeyDown}
			fullWidth
			// themeMESF's dense margin would drop the input below the camera button
			margin={touch ? "none" : undefined}
			size={touch ? "medium" : "small"}
			disabled={assetId == null || mutation.isLoading}
			autoComplete="off"
			// Scanner types as a keyboard; "none" keeps Windows' touch keyboard shut.
			slotProps={touch ? { htmlInput: { inputMode: "none" } } : undefined}
			sx={
				touch
					? { "& .MuiInputBase-root": { height: 56, fontSize: 22 } }
					: undefined
			}
			error={code.length > 0 && !parsed}
			helperText={helper}
		/>
	);

	return (
		<MesfModal title="SCAN ENTRY BUNDLE TAG" open={open} handleClose={close}>
			<MesfModal.Content
				dividers
				// Paper caps at 100vw - 64px
				style={{
					padding: "20px 30px",
					minWidth: touch ? "min(760px, calc(100vw - 64px))" : undefined,
				}}
			>
				{touch ? (
					<Stack
						direction={{ xs: "column", sm: "row" }}
						gap={1.5}
						alignItems={{ xs: "stretch", sm: "flex-start" }}
					>
						{field}
						<input
							ref={photoRef}
							type="file"
							accept="image/*"
							capture="environment"
							hidden
							onChange={onPhoto}
						/>
						<Button
							variant="outlined"
							color="inherit"
							startIcon={<PhotoCameraRounded />}
							onClick={() => photoRef.current?.click()}
							disabled={assetId == null || reading || mutation.isLoading}
							sx={{ height: 56, flexShrink: 0, whiteSpace: "nowrap" }}
						>
							{reading ? "Reading photo…" : "Scan with tablet camera"}
						</Button>
					</Stack>
				) : (
					field
				)}
				{touch && (reading || (parsed && previewQuery.isFetching)) && (
					<LinearProgress sx={{ mt: 1 }} aria-label="Checking tag" />
				)}
				{touch && (
					<Box aria-live="polite">
						{added && (
							<Alert severity="success" sx={{ mt: 2, fontSize: 18 }}>
								{added}
							</Alert>
						)}
						{blocked && (
							<Alert severity="error" sx={{ mt: 2, fontSize: 18 }}>
								{blocked}
							</Alert>
						)}
						{photoAlert && (
							<Alert
								severity={photoAlert.severity}
								sx={{ mt: 2, fontSize: 18 }}
							>
								{photoAlert.text}
							</Alert>
						)}
						{previewQuery.error && (
							<Alert
								severity="error"
								sx={{ mt: 2 }}
								action={
									<Button onClick={() => void previewQuery.refetch()}>
										Retry
									</Button>
								}
							>
								{previewQuery.error.message}
							</Alert>
						)}
						{mutation.error && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{mutation.error.message}
							</Alert>
						)}
					</Box>
				)}
				<Box
					sx={{
						mt: 2,
						p: 1.5,
						border: "1px solid #e5e7eb",
						borderRadius: 1,
						backgroundColor: "#fafafa",
					}}
				>
					<Typography
						sx={{
							fontSize: "0.62rem",
							fontWeight: 700,
							color: "#607080",
							textTransform: "uppercase",
							letterSpacing: 0.5,
							mb: 1,
						}}
					>
						Preview
					</Typography>
					<Grid2 container spacing={1.5}>
						<PreviewRow
							touch={touch}
							label="Tag No"
							value={parsed?.tagNo ?? EMPTY}
						/>
						{partNumber != null && (
							<PreviewRow
								touch={touch}
								label="Part #"
								value={partNumber}
								valid={parsed?.partNumber != null ? partKnown : null}
							/>
						)}
						<PreviewRow
							touch={touch}
							label="Section"
							value={parsed?.sectionName ?? EMPTY}
							valid={sectionKnown}
						/>
						<PreviewRow
							touch={touch}
							label="Grade"
							value={parsed?.gradeName ?? EMPTY}
							valid={gradeKnown}
						/>
						<PreviewRow
							touch={touch}
							label="Heat #"
							value={parsed?.heatNo ?? EMPTY}
						/>
						<PreviewRow
							touch={touch}
							label="Length"
							value={
								parsed
									? (formatEntryLength(
											parsed.millLengthIn,
											parsed.partNumber != null,
										) ?? EMPTY)
									: EMPTY
							}
						/>
						<PreviewRow
							touch={touch}
							label="Pieces"
							value={parsed?.pieces != null ? String(parsed.pieces) : EMPTY}
						/>
						{touch && (
							<PreviewRow
								touch
								label="Work order"
								value={
									preview ? (
										<WorkOrderMatchLabel match={preview.WorkOrderMatch} />
									) : (
										EMPTY
									)
								}
							/>
						)}
					</Grid2>
				</Box>
			</MesfModal.Content>

			<MesfModal.Actions style={{ padding: "20px 30px" }}>
				<Grid2
					container
					spacing={2}
					justifyContent="flex-end"
					sx={touch ? { flex: 1 } : undefined}
				>
					<Grid2 size={touch ? { xs: 6, sm: 4 } : { md: 3, xs: 12 }}>
						<Button
							fullWidth
							variant="contained"
							color="inherit"
							onClick={close}
							disabled={mutation.isLoading}
							sx={touch ? { minHeight: 56, fontSize: 16 } : undefined}
						>
							{touch ? "Close" : "Cancel"}
						</Button>
					</Grid2>
					<Grid2 size={touch ? { xs: 6, sm: 5 } : { md: 3, xs: 12 }}>
						<Button
							ref={saveRef}
							fullWidth
							variant="contained"
							color="success"
							disabled={!canSave}
							onClick={save}
							onKeyDown={touch ? onSaveKeyDown : undefined}
							sx={touch ? { minHeight: 56, fontSize: 16 } : undefined}
						>
							{mutation.isLoading ? "Saving…" : touch ? "Add to queue" : "Save"}
						</Button>
					</Grid2>
				</Grid2>
			</MesfModal.Actions>
		</MesfModal>
	);
};
